import { useEffect, useRef } from 'react';
import type * as SearchInsights from 'search-insights';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { type FragmentContext } from '../../shared/types/fragmentContext';

type InsightsModule = typeof SearchInsights;

type InsightsCredentials = {
    appId: string;
    searchKey: string;
    userToken: string;
};

type InsightsClientHandle = {
    readonly current: Promise<InsightsModule> | undefined;
};

let insightsClientPromise: Promise<InsightsModule> | undefined;
let initializedCredentialsKey: string | undefined;
let credentialsGeneration = 0;

const loadAndInitSearchInsights = async (
    credentials: InsightsCredentials,
    generation: number,
): Promise<InsightsModule> => {
    const searchInsights = await import('search-insights');

    if (generation !== credentialsGeneration) {
        return searchInsights;
    }

    const client = searchInsights.default;

    client('init', {
        appId: credentials.appId,
        apiKey: credentials.searchKey,
    });
    client('setUserToken', credentials.userToken);

    return searchInsights;
};

const getSearchInsights = async (credentials: InsightsCredentials): Promise<InsightsModule> => {
    const credentialsKey = `${credentials.appId}:${credentials.searchKey}:${credentials.userToken}`;

    if (insightsClientPromise && initializedCredentialsKey === credentialsKey) {
        return insightsClientPromise;
    }

    const generation = credentialsGeneration + 1;
    credentialsGeneration = generation;
    const currentAttempt: { promise?: Promise<InsightsModule> } = {};
    const attempt = (async (): Promise<InsightsModule> => {
        try {
            return await loadAndInitSearchInsights(credentials, generation);
        } catch (error) {
            if (
                insightsClientPromise === currentAttempt.promise &&
                initializedCredentialsKey === credentialsKey
            ) {
                insightsClientPromise = undefined;
                initializedCredentialsKey = undefined;
            }

            throw error;
        }
    })();

    currentAttempt.promise = attempt;
    initializedCredentialsKey = credentialsKey;
    insightsClientPromise = attempt;

    return attempt;
};

const resetInsightsClientForTests = (): void => {
    insightsClientPromise = undefined;
    initializedCredentialsKey = undefined;
    credentialsGeneration = 0;
};

const useInsightsClient = (): InsightsClientHandle => {
    const { algoliaTracking } = useFragmentContext<FragmentContext>();
    const appId = algoliaTracking?.appId;
    const searchKey = algoliaTracking?.searchKey;
    const userToken = algoliaTracking?.userToken;
    const credentialsRef = useRef<InsightsCredentials | undefined>(undefined);
    credentialsRef.current =
        appId && searchKey && userToken ? { appId, searchKey, userToken } : undefined;

    const insightsClientRef = useRef<InsightsClientHandle | undefined>(undefined);

    if (!insightsClientRef.current) {
        insightsClientRef.current = {
            get current(): Promise<InsightsModule> | undefined {
                const credentials = credentialsRef.current;

                if (!credentials) {
                    return undefined;
                }

                return getSearchInsights(credentials);
            },
        };
    }

    useEffect(() => {
        const insightsPromise = insightsClientRef.current?.current;

        if (!insightsPromise) {
            return;
        }

        void (async (): Promise<void> => {
            try {
                await insightsPromise;
            } catch {
                // Tracking callers report this failure. The next read of current retries.
            }
        })();
    }, [algoliaTracking]);

    return insightsClientRef.current;
};

export { resetInsightsClientForTests, useInsightsClient };
